import { useState, useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import moment from 'moment-timezone'
import { dateFormats } from '../consts'
import { parseDateStringInEastern, parseDateValue, toExclusiveEndDateISO } from '../services/date'
import logger from '../utils/logger'

const STORAGE_PREFIX = 'dateRange:'

/**
 * Custom hook to manage date range selection with sessionStorage persistence.
 * The selected range survives a page refresh (per route, keyed on pathname) without
 * exposing start/end dates in the browser URL. Falls back to in-memory state when
 * router context or sessionStorage is unavailable.
 */
export function useDateRangeWithURL (initialStart, initialEnd) {
  let location

  try {
    location = useLocation()
  } catch (error) {
    location = { pathname: '' }
  }

  const storageKey = `${STORAGE_PREFIX}${location.pathname || 'default'}`

  const initialDates = useMemo(() => {
    let parsedStartDate = initialStart
    let parsedEndDate = initialEnd

    let stored = null
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (raw) stored = JSON.parse(raw)
    } catch (error) {
      stored = null
    }

    if (stored) {
      if (stored.startDate) {
        const parsed = parseDateStringInEastern(stored.startDate)
        if (parsed) parsedStartDate = parsed
      }

      if (stored.endDate) {
        const parsed = parseDateStringInEastern(stored.endDate)
        if (parsed) parsedEndDate = parsed
      }
    }

    return { parsedStartDate, parsedEndDate }
  }, [storageKey, initialStart, initialEnd])

  const [startDate, setStartDate] = useState(initialDates.parsedStartDate)
  const [endDate, setEndDate] = useState(initialDates.parsedEndDate)

  const formatDateForStorage = (date) => {
    if (!date) return null
    return moment(date).tz(dateFormats.timezone).format('YYYY-MM-DD')
  }

  useEffect(() => {
    if (!startDate || !endDate) return

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        startDate: formatDateForStorage(startDate),
        endDate: formatDateForStorage(endDate)
      }))
    } catch (error) {
      logger.warn('Failed to persist date range to sessionStorage')
    }
  }, [startDate, endDate, storageKey])

  const startDateString = useMemo(() => {
    if (!startDate) return null
    try {
      return moment(startDate).tz(dateFormats.timezone).startOf('day').toDate().toISOString()
    } catch (error) {
      logger.warn('Invalid startDate for ISO conversion:', startDate)
      return null
    }
  }, [startDate])

  const endDateString = useMemo(() => {
    if (!endDate) return null
    try {
      return toExclusiveEndDateISO(endDate)
    } catch (error) {
      logger.warn('Invalid endDate for ISO conversion:', endDate)
      return null
    }
  }, [endDate])

  const handleStartDateChange = (e) => {
    setStartDate(parseDateValue(e))
  }

  const handleEndDateChange = (e) => {
    setEndDate(parseDateValue(e))
  }

  const isDateRangeValid = useMemo(() =>
    !startDate || !endDate || new Date(startDate) <= new Date(endDate),
  [startDate, endDate])

  return {
    startDate,
    endDate,
    startDateString,
    endDateString,
    handleStartDateChange,
    handleEndDateChange,
    isDateRangeValid
  }
}

export default useDateRangeWithURL
